import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, MessageCircle, Moon, Sun, X } from "lucide-react";
import { useApp } from "../context/AppContext";
import { scrollToId } from "../lib/site";

const LangSwitch = () => {
  const { lang, setLang } = useApp();
  return (
    <div className="flex items-center rounded-full border border-border bg-secondary/60 p-1" data-testid="lang-switcher">
      {["id", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-switch-${l}`}
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${
            lang === l ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const ThemeToggle = () => {
  const { theme, setTheme } = useApp();
  const dark = theme === "dark";
  return (
    <button
      data-testid="theme-toggle"
      aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
      onClick={() => setTheme(dark ? "light" : "dark")}
      className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-secondary/60 text-foreground transition-colors duration-200 hover:bg-secondary"
    >
      {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
};

export const Header = () => {
  const { t, waLink } = useApp();
  const [open, setOpen] = useState(false);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-border bg-white/80 backdrop-blur-xl dark:bg-black/70" data-testid="site-header">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 md:px-10">
        <button onClick={() => go("beranda")} className="flex items-center" data-testid="site-logo" aria-label="PT. Natama Trans Asia">
          <span className="flex items-center rounded-xl border border-border/60 bg-white px-3 py-1.5 shadow-sm">
            <img src="/logo.png" alt="PT. Natama Trans Asia — Comfort in Journey" className="hidden h-8 w-auto sm:block" />
            <img src="/logo-mark.png" alt="NTA" className="h-8 w-auto sm:hidden" />
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Main">
          {t.nav.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-link-${item.id}`}
              onClick={() => go(item.id)}
              className="text-sm font-semibold text-muted-foreground transition-colors duration-200 hover:text-foreground"
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:block"><LangSwitch /></div>
          <ThemeToggle />
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-header-cta"
            className="hidden items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground transition-all duration-200 hover:-translate-y-0.5 hover:opacity-90 md:flex"
          >
            <MessageCircle className="h-4 w-4" />
            {t.headerCta}
          </a>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-border lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-border bg-background/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="space-y-1 px-6 py-6">
              {t.nav.map((item) => (
                <button
                  key={item.id}
                  data-testid={`mobile-nav-link-${item.id}`}
                  onClick={() => go(item.id)}
                  className="block w-full rounded-lg px-3 py-3 text-left font-display text-lg font-medium hover:bg-secondary"
                >
                  {item.label}
                </button>
              ))}
              <div className="flex items-center justify-between pt-4">
                <LangSwitch />
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-mobile-cta"
                  className="flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground"
                >
                  <MessageCircle className="h-4 w-4" />
                  {t.headerCta}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
